import type React from 'react'
import {
  ArrowBoldIcon,
  ChatIcon,
  EnvelopeIcon,
  MobileFreeIcon,
  MobileIcon,
} from '@/components/icons'
import { useCookieConsent } from '@/landing/contexts/CookieConsentContext'
import { useLanguage } from '@/landing/contexts/LanguageContext'
import type { FooterProps } from '@/types/components'
import { getCurrentYear } from '@/utils/dateUtils'

export const Footer: React.FC<FooterProps> = ({ translations }) => {
  const { t } = useLanguage()
  const { openPreferences } = useCookieConsent()
  const currentYear = getCurrentYear()

  // Get translations with fallbacks
  const getTranslation = (key: string, fallback: string) => {
    return translations?.footer?.[key] || t(`footer.${key}`) || fallback
  }

  return (
    <footer className="flex flex-col w-full">
      <div className="bg-primary py-3" />
      <div className="flex flex-col md:flex-row">
        {/* Contact Section */}
        <div className="bg-[#1C2737] text-white p-8 md:w-1/2">
          <div className="h-full grid grid-rows-[3fr_1fr]">
            {/* First Row - Contact Data (flex-start) */}
            <div className="flex flex-col justify-start">
              <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0">
                <a href="https://www.sheet-pile.com" target="_blank" rel="noopener noreferrer">
                  <img
                    src="/images/sheet_pile_logo_dark.svg"
                    alt="Sheet Pile LLC"
                    className="h-8 filter brightness-0 invert"
                  />
                </a>
                <img
                  src="/images/sheet_pile_app_logo_light.png"
                  alt="SheetPile APP"
                  className="h-8"
                />
              </div>

              <div>
                <p className="text-xl font-black uppercase text-white mb-3">
                  {getTranslation('contact_data', 'Contact Data')}
                </p>
                <div className="flex items-center mb-3">
                  <EnvelopeIcon className="h-5 w-5 mr-4 text-white" />
                  <a
                    href="mailto:info@sheet-pile.com"
                    className="relative inline-block group"
                    aria-label="Send email to info@sheet-pile.com"
                  >
                    <span className="text-[15px] group-hover:text-white/90 transition-colors">
                      info@sheet-pile.com
                    </span>
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-white group-hover:w-full transition-all duration-300 ease-in-out" />
                  </a>
                </div>
                <div className="flex items-center mb-3">
                  <MobileFreeIcon className="h-5 w-5 mr-4 text-white" />
                  <a
                    href="tel:+18666667453"
                    className="relative inline-block group"
                    aria-label="Call toll free: 866.666.7453"
                  >
                    <span className="text-[15px] group-hover:text-white/90 transition-colors">
                      Toll Free: 866.666.7453
                    </span>
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-white group-hover:w-full transition-all duration-300 ease-in-out" />
                  </a>
                </div>
                <div className="flex items-center mb-3">
                  <MobileIcon className="h-5 w-5 mr-4 text-white" />
                  <a
                    href="tel:+15122431228"
                    className="relative inline-block group"
                    aria-label="Call: +1 512.243.1228"
                  >
                    <span className="text-[15px] group-hover:text-white/90 transition-colors">
                      +1 512.243.1228
                    </span>
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-white group-hover:w-full transition-all duration-300 ease-in-out" />
                  </a>
                </div>
              </div>
            </div>

            {/* Second Row - Zoom Connection Gate (flex-end bottom) */}
            <div className="flex justify-end items-end">
              <a
                href="https://www.sheet-pile.com/zoom"
                className="flex items-center gap-2 bg-primary hover:bg-primary/80 text-white px-4 py-3 rounded-lg transition-colors"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open Zoom Connect Gate"
              >
                <ChatIcon className="h-5 w-5" />
                <span className="text-sm font-bold hidden sm:inline">
                  {getTranslation('zoom_connect', 'ZOOM CONNECT GATE')}
                </span>
              </a>
            </div>
          </div>
        </div>

        {/* Links Section */}
        <div className="bg-white p-8 md:w-1/2">
          <p className="text-xl font-black uppercase mb-4">
            {getTranslation('useful_links', 'Useful Links')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <ul className="space-y-3">
                <li className="relative pl-4 group">
                  <ArrowBoldIcon className="absolute left-0 top-1.5 h-3 w-3 text-gray-800 group-hover:text-primary transition-colors duration-300" />
                  <a
                    href="https://www.sheet-pile.com/solutions"
                    className="inline-block relative"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="relative z-10 text-[15px] text-gray-800 group-hover:text-primary transition-colors duration-300">
                      {getTranslation('innovative_solutions', 'Innovative solutions')}
                    </span>
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300 ease-in-out" />
                  </a>
                </li>
                <li className="relative pl-4 group">
                  <ArrowBoldIcon className="absolute left-0 top-1.5 h-3 w-3 text-gray-800 group-hover:text-primary transition-colors duration-300" />
                  <a
                    href="https://www.sheet-pile.com/products"
                    className="inline-block relative"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="relative z-10 text-[15px] text-gray-800 group-hover:text-primary transition-colors duration-300">
                      {getTranslation('products', 'Products')}
                    </span>
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300 ease-in-out" />
                  </a>
                </li>
                <li className="relative pl-4 group">
                  <ArrowBoldIcon className="absolute left-0 top-1.5 h-3 w-3 text-gray-800 group-hover:text-primary transition-colors duration-300" />
                  <a
                    href="https://www.sheet-pile.com/about"
                    className="inline-block relative"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="relative z-10 text-[15px] text-gray-800 group-hover:text-primary transition-colors duration-300">
                      {getTranslation('about_us', 'About us')}
                    </span>
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300 ease-in-out" />
                  </a>
                </li>
                {/* TODO: uncomment when installation page is ready
                <li className="relative pl-4 group">
                  <ArrowBoldIcon className="absolute left-0 top-1.5 h-3 w-3 text-gray-800 group-hover:text-primary transition-colors duration-300" />
                  <a href="/installation" className="inline-block relative">
                    <span className="relative z-10 text-[15px] text-gray-800 group-hover:text-primary transition-colors duration-300">
                      {getTranslation('installation_guidelines', 'Installation Guidelines')}
                    </span>
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300 ease-in-out" />
                  </a>
                </li>
                */}
              </ul>
            </div>
            <div>
              <ul className="space-y-3">
                <li className="relative pl-4 group">
                  <ArrowBoldIcon className="absolute left-0 top-1.5 h-3 w-3 text-gray-800 group-hover:text-primary transition-colors duration-300" />
                  <a
                    href="https://www.sheet-pile.com/resources"
                    className="inline-block relative"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="relative z-10 text-[15px] text-gray-800 group-hover:text-primary transition-colors duration-300">
                      {getTranslation('resources', 'Resources')}
                    </span>
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300 ease-in-out" />
                  </a>
                </li>
                <li className="relative pl-4 group">
                  <ArrowBoldIcon className="absolute left-0 top-1.5 h-3 w-3 text-gray-800 group-hover:text-primary transition-colors duration-300" />
                  <a
                    href="https://www.sheet-pile.com/tools"
                    className="inline-block relative"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="relative z-10 text-[15px] text-gray-800 group-hover:text-primary transition-colors duration-300">
                      {getTranslation('tools', 'Tools')}
                    </span>
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300 ease-in-out" />
                  </a>
                </li>
                <li className="relative pl-4 group">
                  <ArrowBoldIcon className="absolute left-0 top-1.5 h-3 w-3 text-gray-800 group-hover:text-primary transition-colors duration-300" />
                  <a href="/privacy" className="inline-block relative">
                    <span className="relative z-10 text-[15px] text-gray-800 group-hover:text-primary transition-colors duration-300">
                      {getTranslation('privacy_policy', 'Privacy policy')}
                    </span>
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300 ease-in-out" />
                  </a>
                </li>
                <li className="relative pl-4 group">
                  <ArrowBoldIcon className="absolute left-0 top-1.5 h-3 w-3 text-gray-800 group-hover:text-primary transition-colors duration-300" />
                  <a href="/terms" className="inline-block relative">
                    <span className="relative z-10 text-[15px] text-gray-800 group-hover:text-primary transition-colors duration-300">
                      {getTranslation('terms_of_use', 'Terms of use')}
                    </span>
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300 ease-in-out" />
                  </a>
                </li>
                <li className="relative pl-4 group">
                  <ArrowBoldIcon className="absolute left-0 top-1.5 h-3 w-3 text-gray-800 group-hover:text-primary transition-colors duration-300" />
                  <button
                    onClick={openPreferences}
                    className="inline-block relative text-left cursor-pointer"
                    type="button"
                  >
                    <span className="relative z-10 text-[15px] text-gray-800 group-hover:text-primary transition-colors duration-300">
                      {getTranslation('cookie_preferences', 'Cookie Preferences')}
                    </span>
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300 ease-in-out" />
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <ul className="space-y-3">
                <li className="relative pl-4 group">
                  <ArrowBoldIcon className="absolute left-0 top-1.5 h-3 w-3 text-gray-800 group-hover:text-primary transition-colors duration-300" />
                  <a
                    href="https://www.sheet-pile.com/patents-and-licenses"
                    className="inline-block relative"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="relative z-10 text-[15px] text-gray-800 group-hover:text-primary transition-colors duration-300">
                      {getTranslation('patents_licenses', 'Patents & Licenses')}
                    </span>
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300 ease-in-out" />
                  </a>
                </li>
                <li className="relative pl-4 group">
                  <ArrowBoldIcon className="absolute left-0 top-1.5 h-3 w-3 text-gray-800 group-hover:text-primary transition-colors duration-300" />
                  <a
                    href="https://www.sheet-pile.com/contact"
                    className="inline-block relative"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="relative z-10 text-[15px] text-gray-800 group-hover:text-primary transition-colors duration-300">
                      {getTranslation('contact', 'Contact')}
                    </span>
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300 ease-in-out" />
                  </a>
                </li>
                <li className="relative pl-4 group">
                  <ArrowBoldIcon className="absolute left-0 top-1.5 h-3 w-3 text-gray-800 group-hover:text-primary transition-colors duration-300" />
                  <a href="/login" className="inline-block relative">
                    <span className="relative z-10 text-[15px] text-gray-800 group-hover:text-primary transition-colors duration-300">
                      {getTranslation('login', 'Login')}
                    </span>
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300 ease-in-out" />
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-black text-white py-2 px-5 text-center text-[15px]">
        © {currentYear} Sheet-Pile LLC. {getTranslation('rights_reserved', 'All Rights Reserved')}
      </div>
    </footer>
  )
}
